import React from 'react';
import { Link } from 'react-router-dom';
import { ThemedCard } from '../components/themed';
import { Lock, Mail, Users, AlertCircle } from 'lucide-react';
import { H1 } from '../components/Heading';
import { APP_ROUTES, marketingUrl } from '../config/routes';
import ThemeToggle from '../components/ThemeToggle';

interface SignupDisabledProps {
  mode: 'disabled' | 'invitation_only' | 'whitelist' | 'open';
  allowedDomains?: string[];
}

/**
 * SignupDisabled page - shown when signup is disabled or restricted.
 * Provides helpful information about how to get access.
 */
const SignupDisabled: React.FC<SignupDisabledProps> = ({ mode, allowedDomains = [] }) => (
  <div className="min-h-screen flex items-center justify-center surface-secondary relative">
    <div className="absolute top-4 right-4">
      <ThemeToggle />
    </div>
    <ThemedCard className="w-96">
      <div className="p-6">
        <div className="flex justify-center mb-4">
          <div className="h-16 w-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
            <Lock className="h-8 w-8 text-orange-700 dark:text-orange-400" />
          </div>
        </div>

        {mode === 'disabled' && (
          <>
            <H1 className="text-center mb-2">Signups Currently Disabled</H1>
            <p className="text-sm text-center text-secondary mb-6">
              Public signups are temporarily disabled. We're preparing for launch and will enable
              signups soon.
            </p>

            <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg p-4 mb-6">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
                <div className="text-sm text-sky-800 dark:text-sky-200">
                  <p className="font-medium mb-2">Want Early Access?</p>
                  <p className="text-sky-700 dark:text-sky-300">
                    <a
                      href={marketingUrl('/contact?subject=support')}
                      className="underline hover:text-sky-900 dark:hover:text-sky-100"
                    >
                      Contact us
                    </a>{' '}
                    to request an invitation.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {mode === 'invitation_only' && (
          <>
            <H1 className="text-center mb-2">Invitation-Only Access</H1>
            <p className="text-sm text-center text-secondary mb-6">
              Coldsea Deploy is currently in private beta. You need an invitation to create an
              account.
            </p>

            <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg p-4 mb-6">
              <div className="flex gap-2">
                <Mail className="h-5 w-5 text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
                <div className="text-sm text-sky-800 dark:text-sky-200">
                  <p className="font-medium mb-2">How to Get Access</p>
                  <div className="space-y-2 text-sky-700 dark:text-sky-300">
                    <p>1. Contact your organization administrator</p>
                    <p>2. Ask them to send you an invitation email</p>
                    <p>3. Click the link in your invitation to set up your account</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg p-4">
              <p className="text-sm text-zinc-700 dark:text-zinc-300">
                <strong>Don't have an invitation?</strong>
                <br />
                <a
                  href={marketingUrl('/contact?subject=support')}
                  className="text-sky-600 dark:text-sky-400 hover:underline"
                >
                  Contact us
                </a>{' '}
                to request access.
              </p>
            </div>
          </>
        )}

        {mode === 'whitelist' && (
          <>
            <H1 className="text-center mb-2">Beta Access - Approved Domains Only</H1>
            <p className="text-sm text-center text-secondary mb-6">
              Signups are currently limited to approved email domains.
            </p>

            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-6">
              <div className="flex gap-2">
                <Users className="h-5 w-5 text-orange-700 dark:text-orange-400 shrink-0 mt-0.5" />
                <div className="text-sm text-orange-800 dark:text-orange-200">
                  <p className="font-medium mb-2">Approved Domains</p>
                  {allowedDomains.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1 text-orange-700 dark:text-orange-300">
                      {allowedDomains.map((domain) => (
                        <li key={domain}>@{domain}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-orange-700 dark:text-orange-300">
                      Contact us to add your domain to the approved list.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg p-4">
              <p className="text-sm text-sky-800 dark:text-sky-200">
                <strong>Email from an approved domain?</strong>
                <br />
                <Link
                  to={APP_ROUTES.SIGNUP}
                  className="text-sky-600 dark:text-sky-400 hover:underline"
                >
                  Try signing up →
                </Link>
              </p>
            </div>

            <div className="mt-4 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg p-4">
              <p className="text-sm text-zinc-700 dark:text-zinc-300">
                <strong>Domain not listed?</strong>
                <br />
                <a
                  href={marketingUrl('/contact?subject=support')}
                  className="text-sky-600 dark:text-sky-400 hover:underline"
                >
                  Contact us
                </a>{' '}
                to request access for your organization.
              </p>
            </div>
          </>
        )}

        <div className="mt-6 space-y-3">
          <Link
            to={APP_ROUTES.LOGIN}
            className="block w-full px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-center font-medium rounded-lg transition-colors"
          >
            Back to Login
          </Link>

          <div className="text-center">
            <a
              href={marketingUrl('/contact?subject=support')}
              className="text-sm text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </ThemedCard>
  </div>
);

export default SignupDisabled;
